import { z } from 'zod';
import { productPurchaseSchema } from '../../common';
import { nameSchema, priceSchema, quantitySchema } from '../../validation';

/**
 * A single line on a Billing. Items are immutable after `BillingService.finalize`.
 * The Billing total is the sum of all items, plus tax.
 *
 * Item types map directly to what's being charged. Negative-amount types
 * (`proration_credit`, `discount`) carry a positive `unitPrice` / `quantity`
 * but a pre-negated `amount`. The pricing service is responsible for
 * computing `amount` correctly for negative lines.
 */
export const billingItemTypeSchema = z.enum([
  'subscription_base',
  /** Committed vehicle seats — vehicle-unit period plans. */
  'subscription_vehicle',
  /**
   * Vehicles beyond the committed seat count.
   *
   * Named without a `_vehicle_` segment because it predates per-order
   * billing; renaming it would orphan every historical Billing line, so
   * the order counterpart is `subscription_order_overage` and this
   * one keeps its name. Read it as "vehicle overage".
   */
  'subscription_overage',
  /** Committed / purchased order volume. */
  'subscription_order',
  /** Orders beyond the committed volume for the period. */
  'subscription_order_overage',
  'addon_recurring',
  'addon_one_time',
  'proration_credit',
  'proration_charge',
  'discount',
  'other',
]);
export type BillingItemType = z.infer<typeof billingItemTypeSchema>;

export const billingItemSchema = z.object({
  type: billingItemTypeSchema,
  /**
   * What this line sold. Addon lines set `purchase.addonCode`.
   * Optional on manual/discount lines with no catalog tie.
   */
  purchase: productPurchaseSchema.optional(),
  /**
   * Human-readable line description (used in receipts).
   *
   * **English, and persisted as written.** Produced by
   * `formatBillingLine` in `@hero/core`.
   */
  description: nameSchema,
  /** Number of units (always >= 0; direction comes from `type`). */
  quantity: quantitySchema,
  /** Per-unit price in the Billing's currency. */
  unitPrice: priceSchema,
  /** Line amount in the Billing's currency. For positive types: quantity * unitPrice. */
  amount: priceSchema,
  /** Optional inline note. */
  note: nameSchema.optional(),
});
export type BillingItem = z.infer<typeof billingItemSchema>;
